import { SHAPEFILE_BUFFER_UNIT_LOCAL_STORAGE_KEY } from "constants/app.constants";
import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";

import { METRES_PER_LABEL_UNIT } from "./shapefileFilterPredicate";

/**
 * The unit the buffer switch was last left on, remembered per browser and
 * subscribed to rather than read off localStorage wherever it is wanted.
 *
 * It is read on two sides of the screen that never render together: the buffer
 * control's own switch, and every place a boundary predicate is put into words
 * — the active-filter chip and the filter tree. While it was read straight out
 * of localStorage, the only thing that brought the labels up to date was the
 * switch re-committing the buffer onto the predicate, which rebuilt the chips.
 * Staging took that write away (EVA-206), so a flip left the chip reading
 * "(+500 m)" until some unrelated filter change happened to rebuild it. This
 * store is the subscription that used to be doing without.
 *
 * localStorage stays the durable copy and this store the only thing that
 * writes it, so the two cannot answer differently.
 */
interface ShapefileBufferUnitStore {
  /** The remembered choice, or nothing while none has been made. */
  remembered: Maybe<string>;
  /** Remembers a pick. The one writer of the localStorage key. */
  remember: (unit: string) => void;
  /** Drops what is held and reads the durable copy again. */
  reset: () => void;
}

/**
 * A remembered value only counts while it is still one a buffer can be said
 * in. Read off the conversion table's own keys rather than with `in`, which
 * answers yes to "constructor" and every other name every object carries.
 * Whether it is one of the two on offer is a separate question, settled
 * against the pair being shown.
 */
function usableUnit(value: Maybe<string>): Maybe<string> {
  return value && Object.prototype.hasOwnProperty.call(METRES_PER_LABEL_UNIT, value)
    ? value
    : null;
}

function rememberedFromStorage(): Maybe<string> {
  try {
    return usableUnit(localStorage.getItem(SHAPEFILE_BUFFER_UNIT_LOCAL_STORAGE_KEY));
  } catch {
    // Reading has to be as forgiving as writing, and for a sharper reason: a
    // browser with site data blocked throws from the localStorage getter
    // itself, and this runs while the store is being created — which happens
    // as the root reducer's module graph is evaluated, before React has
    // mounted and before there is an error boundary to catch anything. An
    // unguarded read here takes the whole app down rather than one control.
    return null;
  }
}

export const useShapefileBufferUnitStore = create<ShapefileBufferUnitStore>()(
  subscribeWithSelector((set) => ({
    remembered: rememberedFromStorage(),
    remember: (unit) => {
      // Settled before it is written, so the durable copy can never hold a
      // unit this store would refuse to answer with.
      const usable = usableUnit(unit);
      if (!usable) {
        return;
      }
      try {
        localStorage.setItem(SHAPEFILE_BUFFER_UNIT_LOCAL_STORAGE_KEY, usable);
      } catch {
        // Remembering the unit is a convenience; a full or blocked store is no
        // reason to drop the buffer the user is in the middle of setting, or
        // to leave the labels spelling the unit they were on.
      }
      set({ remembered: usable });
    },
    reset: () => set({ remembered: rememberedFromStorage() })
  }))
);

export const selectRememberedShapefileBufferUnit = (state: ShapefileBufferUnitStore) =>
  state.remembered;

/** Remembers the unit the buffer switch was just set to. */
export function rememberShapefileBufferUnit(unit: string): void {
  useShapefileBufferUnitStore.getState().remember(unit);
}
